// The dice bag (slice 11; design/15-dice-bag.md, binding decisions 1, 2, 12,
// 17–20, 29, 30): an expandable side panel — collapsed, a dice icon; open,
// My Dice and Add Dice, the rows, the label, Reset my dice, and the table's
// shared log. Each row is quantity, the die face as the roll button, the
// sign, the modifier and what the row last threw. The bag is account-wide.
// Every throw is asked of the server (`roll.request`) and comes back as an
// event; the browser never decides a face. The DM's rolls are private by
// default, and the bag makes the state impossible to miss. The log keeps
// every roll; the DM clears its VIEW, and anyone can show the cleared rolls
// again; the DM can amend only a private roll of their own, and save the
// whole log as a file. Saved roll buttons and group roll requests (issue 1049):
// a labelled row is one named roll fired with one click; the DM asks chosen
// players for a roll, each asked player gets a prompt here and answers it as
// their own roll, and the DM watches who has rolled.
//
// The site's look (issue 1541, slice 2): the DM's Public / Private toggle at the top; a 4 × 2 grid of quick dice (d4 to
// d20, d%, and custom, which goes to the expression), each throwing one of itself with what it last threw beneath; an
// expression field ("2d6 + 3") beside a gold ROLL; the saved roll buttons; the log as cards edged in the roller's
// colour; and under it the bag's own rows, each die with its count and modifier and the custom dice, as "My dice". Every
// throw is still one `roll.request` built the one way, so the label, the privacy, Roll for and the band ride on each.
import { TurnNotifyToggle } from '../../turnNudge';
import { PushToggle } from '../../pwa';
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import {
  BAND_SIDES, CONFIRM_ABOVE, DICE_LIMITS, bandApplies, parseBand, type Band, defaultBag, savedRollRequests, BAG_LIMITS, type PlayerAsk, type RollAsk, formatRoll, notationOf, rerollOf, tally, dmAttackLines, dmSaveLines, resetBag, standardRows, type Actor, type Bag, type DiceRow, type RollRecord, type RollRequest,
  memberLabel,
  NO_ATTACK_RULES, playerAttackLines, playerSaveLines, testLines, type TableMember,
  type AdvantageWord,
} from '@dragons-table/vtt-shared';
import { fetchBag, fetchLogText, saveBag, type LogEntry } from '../../dice';
import { useTableState } from '../../table/TableState';
import { selection } from '../../board/selection';
import { useHolds, useMayAct } from '../../table/mayAct';
import { useNumberWords } from '../../table/numberWords';
import { useShell } from '../context';
import { useLook } from '../useLook';
import { ApplyRoll, AppliedRows, mayApply } from './ApplyParts';
import { AttackForm, DamageProposal, HouseRuleParts, PlayerAttackEntry, SupplyDefence } from './AttackParts';
import { DmFullSwing } from './FullAttackParts';
import { PlayerSaveEntry, SaveRows } from './SaveParts';
import { CheckSection, TokenChecks, parseExtra } from './CheckParts';
import { KarmaParts, TestForm } from './TestParts';
import { StagedDmParts, StagedForm } from './StagedParts';
import { CastForm } from './CastParts';
import { ConjureForm } from './ConjureParts';
import { SystemTestForm } from './MatrixParts';
import { BringOutForm, VehicleTestForm } from './VehicleParts';
import { BlastLauncher } from './BlastParts';
import { ChasePanel, VehicleCombatForm, useVehicleCombat } from './VehicleCombatParts';
import { PlayerTriggerEntry, TriggerRows, triggerText } from './TriggerParts';
import { PlaceDialog } from './PlaceDialog';
import { SaveNpcDialog } from './SaveNpcDialog';
import { ConfirmDialog } from '../Dialogs';
import { useDicePrivate } from '../../dicePrivacy';
import { parseExpression } from '../../diceExpression';
import { BTN, PRIMARY_SM, SECTION_HEAD, TITLE_CAPS } from '../ui';
import { hotDragProps } from '../../board/hotDrag';

const btn = BTN;
const OPEN_KEY = 'vtt.dice.open';

function readOpen(): boolean {
  try { return localStorage.getItem(OPEN_KEY) !== 'closed'; } catch { return true; }
}

/** The die's face as it reads on the button: "d20", "coin", "d?" for the scratch row, a saved die's sides. */
const faceOf = (row: DiceRow): string => (row.die.kind === 'coin' ? 'coin' : row.custom && row.name === null ? 'd?' : `d${row.die.sides}`);
const nameUnder = (row: DiceRow): string => (row.name ?? (row.die.kind === 'coin' ? 'heads/tails' : row.custom ? 'any sides' : `${row.die.sides} sides`));
/** What a row is called in its controls' names: a saved roll button by its title, any other row by its face. */
const titleOf = (row: DiceRow): string => row.label ?? faceOf(row);
/** A part of the roll button being made in Add Dice (issue 1295), as typed. */
type RbPart = { label: string; count: string; sides: string; mod: string };
const NEW_PART: RbPart = { label: '', count: '1', sides: '20', mod: '0' };
/** A bag row's number box: no spinner arrows, which took half its width (typing and the arrow keys still step it). */
const numberBox = 'rounded bg-stone-800 px-1 py-0.5 text-xs [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
/** A row's arrows and ×: a column of small buttons under a mouse, one line at the row's end under a finger. */
const coarseLine = '[@media(pointer:coarse)]:ml-auto [@media(pointer:coarse)]:flex-row';
const tiny = 'rounded border border-stone-700 px-1 text-[0.625rem] leading-4 hover:bg-stone-800 disabled:opacity-40';
/** The quick dice of the grid (issue 1541): one of each, d% the hundred-sided. The eighth square is "custom". */
const GRID_SIDES = [4, 6, 8, 10, 12, 20, 100] as const;
const gridFace = (sides: number) => (sides === 100 ? 'd%' : `d${sides}`);
/** A roll's time as the log shows it, in the viewer's own clock: "6:05 PM". Nothing for a time that does not read. */
const clockOf = (at: string): string | null => {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/**
 * The DM's fudge band (issue 1038, Tom's choice A): private d20 throws land in [low, high], every number in it equally
 * likely, the server's generator still deciding. Set in advance and kept in the bag; players never see it or its mark.
 */
function BandEditor({ band, onChange }: { band: Band | null; onChange: (band: Band | null) => void }) {
  const [low, setLow] = useState(String(band?.min ?? 5));
  const [high, setHigh] = useState(String(band?.max ?? 16));
  // Said after the boxes were put back to the band in force (issue 1429), until the DM types again.
  const [kept, setKept] = useState<Band | null>(null);
  const wanted = parseBand({ min: Number(low), max: Number(high) });
  const set = (on: boolean, l = low, h = high) => {
    if (!on) { onChange(null); return; }
    const b = parseBand({ min: Number(l), max: Number(h) });
    if (b) onChange(b);
  };
  // A ticked band takes a new range only when the DM leaves the two boxes, or presses Enter (issue 1429, its retest):
  // set at every keystroke, typing 18 into the low box made 18 to 18 the band before the high box was reached. A range
  // the band refuses puts the boxes back to the band in force, so they never show a range the rolls are not using.
  // Moving from one box to the other is not leaving.
  const settle = () => {
    if (!band) return;
    if (wanted) { if (wanted.min !== band.min || wanted.max !== band.max) onChange(wanted); return; }
    setLow(String(band.min)); setHigh(String(band.max)); setKept(band);
  };
  const leave = (e: FocusEvent<HTMLSpanElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    settle();
  };
  const enter = (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') { e.preventDefault(); settle(); } };
  const pending = band && wanted && (wanted.min !== band.min || wanted.max !== band.max);
  const typed = (fn: () => void) => { setKept(null); fn(); };
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs" data-testid="dice-band"
      title="Your private d20s, and those you roll for a player, land in this band, every number in it equally likely. Players never see it.">
      <label className="flex items-center gap-1">
        <input type="checkbox" aria-label="Fudge band" checked={band !== null} disabled={!wanted && band === null} onChange={(e) => set(e.target.checked)} />
        Fudge band on private d20s:
      </label>
      {/* The range wraps as one piece, so "to" and its high number never land on separate lines. */}
      <span className="flex items-center gap-1 whitespace-nowrap" onBlur={leave}>
        <input aria-label="Band low" type="number" min={1} max={BAND_SIDES} className="w-12 rounded bg-stone-800 px-1 py-0.5" value={low}
          onKeyDown={enter} onChange={(e) => typed(() => setLow(e.target.value))} />
        to
        <input aria-label="Band high" type="number" min={1} max={BAND_SIDES} className="w-12 rounded bg-stone-800 px-1 py-0.5" value={high}
          onKeyDown={enter} onChange={(e) => typed(() => setHigh(e.target.value))} />
      </span>
      {!wanted && (
        <span className="text-rose-400" data-testid="band-refused">
          1 to {BAND_SIDES}, low first{band ? ` — the band stays ${band.min} to ${band.max}` : ''}
        </span>
      )}
      {wanted && kept && <span className="text-stone-400" data-testid="band-kept">Kept {kept.min} to {kept.max}: that range was not a band.</span>}
      {pending && <span className="text-stone-400" data-testid="band-pending">The band stays {band!.min} to {band!.max} until you leave the boxes or press Enter.</span>}
    </div>
  );
}

/** One row of the bag (design/15 §2). */
function Row({ row, result, onChange, onRoll, onRemove, onMove, first, last }: {
  row: DiceRow; result: string | null; onChange: (r: DiceRow) => void; onRoll: (r: DiceRow, sides: number | null) => void; onRemove: () => void; onMove: (d: -1 | 1) => void; first: boolean; last: boolean;
}) {
  const [sides, setSides] = useState(String(row.die.kind === 'd' ? row.die.sides : 3));
  const scratch = row.custom && row.name === null;
  const sign = row.modifier < 0 ? '-' : '+';
  const mag = Math.abs(row.modifier);
  if (row.label) {
    // A saved roll button (issue 1049): its title is the button; what it throws is fixed when it was saved.
    return (
      <li className="flex flex-wrap items-center gap-1 py-0.5" data-testid={`die-${row.id}`}>
        <button type="button" className="flex h-9 min-w-24 max-w-48 flex-col items-center justify-center rounded border border-sky-500 bg-stone-800 px-2 text-sm font-bold leading-4 hover:bg-stone-700"
          aria-label={`Roll ${row.label}`} onClick={() => onRoll(row, null)}>
          <span className="max-w-full truncate">{row.label}</span>
          <span className="text-[0.5625rem] font-normal leading-3 text-stone-400">{(row.parts ?? [row]).map((p) => notationOf(p.die, p.quantity, p.die.kind === 'coin' ? 0 : p.modifier)).join(' · ')}</span>
        </button>
        <span className="min-w-8 text-sm font-semibold" data-testid={`result-${row.id}`}>{result ?? ''}</span>
        <span className={`ml-auto flex flex-col gap-0.5 ${coarseLine}`}>
          <span className="flex gap-0.5">
            <button type="button" className={tiny} aria-label={`Move ${titleOf(row)} up`} disabled={first} onClick={() => onMove(-1)}>↑</button>
            <button type="button" className={tiny} aria-label={`Move ${titleOf(row)} down`} disabled={last} onClick={() => onMove(1)}>↓</button>
          </span>
          <button type="button" className={tiny} aria-label={`Remove ${titleOf(row)}`} onClick={onRemove}>×</button>
        </span>
      </li>
    );
  }
  return (
    // The controls wrap among themselves on a narrow bar; what the die last threw and its arrows stay at the row's
    // end, so a result never sits alone on a line. At the 300 px side bar the whole row is one line.
    // Under a finger (44 px controls) the row wraps too, its arrows and × as one line at the end: on a tablet's narrow bar
    // the arrows' column otherwise squeezed the controls into one per line.
    <li className="flex items-center gap-1 py-0.5 [@media(pointer:coarse)]:flex-wrap" data-testid={`die-${row.id}`}>
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-0.5 [@media(pointer:coarse)]:basis-auto">
      <input aria-label={`Quantity of ${faceOf(row)}`} type="number" min={1} max={DICE_LIMITS.quantity} className={`w-8 ${numberBox}`} value={row.quantity}
        onChange={(e) => onChange({ ...row, quantity: Math.max(1, Math.min(DICE_LIMITS.quantity, Math.floor(Number(e.target.value) || 1))) })} />
      <button type="button" className="flex h-9 w-14 flex-col items-center justify-center rounded border border-stone-600 bg-stone-800 text-sm leading-4 hover:border-amber-700 hover:bg-stone-700"
        aria-label={`Roll ${faceOf(row)}${row.name ? ` (${row.name})` : ''}`} onClick={() => onRoll(row, scratch ? Math.max(2, Math.min(DICE_LIMITS.sides, Math.floor(Number(sides) || 2))) : null)}>
        <span className="font-display font-bold text-vtt-gold">{faceOf(row)}</span>
        <span className="max-w-full truncate text-[0.5625rem] font-normal leading-3 text-stone-400">{nameUnder(row)}</span>
      </button>
      {scratch && <input aria-label="Sides" type="number" min={2} max={DICE_LIMITS.sides} className={`w-9 ${numberBox}`} value={sides} onChange={(e) => setSides(e.target.value)} />}
      {row.die.kind !== 'coin' && (
        <>
          <select aria-label={`Sign for ${faceOf(row)}`} className="w-8 rounded bg-stone-800 px-1 py-0.5 text-xs" value={sign} onChange={(e) => onChange({ ...row, modifier: (e.target.value === '-' ? -1 : 1) * mag })}>
            <option value="+">+</option><option value="-">−</option>
          </select>
          <input aria-label={`Modifier for ${faceOf(row)}`} type="number" min={0} max={DICE_LIMITS.modifier} className={`w-8 ${numberBox}`} value={mag}
            onChange={(e) => onChange({ ...row, modifier: (sign === '-' ? -1 : 1) * Math.max(0, Math.min(DICE_LIMITS.modifier, Math.floor(Number(e.target.value) || 0))) })} />
        </>
      )}
      </span>
      <span className="min-w-6 shrink-0 text-sm font-semibold" data-testid={`result-${row.id}`} title={`What ${faceOf(row)} last threw`}>{result ?? ''}</span>
      <span className={`flex shrink-0 flex-col gap-0.5 ${coarseLine}`}>
        <span className="flex gap-0.5">
          <button type="button" className={tiny} aria-label={`Move ${faceOf(row)} up`} disabled={first} onClick={() => onMove(-1)}>↑</button>
          <button type="button" className={tiny} aria-label={`Move ${faceOf(row)} down`} disabled={last} onClick={() => onMove(1)}>↓</button>
        </span>
        <button type="button" className={tiny} aria-label={`Remove ${faceOf(row)}`} onClick={onRemove}>×</button>
      </span>
    </li>
  );
}

/** A log entry as the table reads it, attributed in the roller's identity colour. */
/**
 * Who sees a private roll, said on its mark (issue 1289): the DM's own is the DM's alone; an answer to the DM's private
 * request is its roller's and the DM's, said to each in their own words.
 */
export function privateTitle(roll: RollRecord, meId: string, nameOf: (a: Actor) => string): string {
  if (roll.ask && !roll.by) return roll.actor.id === meId ? 'Private: only you and the DM see this roll.' : `Private: you and ${nameOf(roll.actor)} see this roll.`;
  if (roll.by) return roll.actor.id === meId ? 'Private: only you and the DM see this roll.' : `Private: you alone see this roll, made for ${nameOf(roll.actor)}.`;
  return 'Private: the DM alone sees this roll.';
}

/**
 * Who an attack's log entry is headed by: the character that attacked, with its player's handle, when that player owns
 * it (qa-runs#46 F2: a player with two characters was headed by the first one seated); else the person, as any roll is.
 */
export function attackHeadName(roll: RollRecord, meId: string, nameOf: (a: Actor) => string): string {
  const attacker = roll.attack?.attacker;
  if (roll.actor.id === meId || roll.actor.former || !attacker || !attacker.owners.includes(roll.actor.id)) return nameOf(roll.actor);
  return roll.actor.handle ? `${attacker.name} (@${roll.actor.handle})` : attacker.name;
}

/**
 * A roll made in minutes the DM later rewound (issue 1169): it stands in the log, and says so on a line of its own
 * just under it — the record itself is never changed.
 */
function withRewoundMark(e: LogEntry, item: ReactNode): ReactNode {
  if (!('rewound' in e) || !e.rewound) return item;
  const id = e.kind === 'roll' ? e.roll.id : e.kind === 'attack' ? e.attack.id : e.kind === 'save' ? e.save.id : e.kind === 'trigger' ? e.trigger.id : '';
  return (
    <Fragment key={`rewound-${id}`}>
      {item}
      <li className="pb-1 pl-2 text-[0.625rem] italic text-sky-300" data-testid={`rewound-${id}`} title="The DM rewound the table after this roll was made. The roll stands.">↺ made before the rewind</li>
    </Fragment>
  );
}

/** A roll's lines in the log, as this viewer's screen shows them; a screen reader is told the same (issue 1175). */
export function rollText(roll: RollRecord, meId: string, nameOf: (a: Actor) => string): string {
  // A trigger's chain (issue 1500): the table's own, so no one's name heads it.
  if (roll.trigger) return triggerText(roll);
  // A success test (issue 1523): its working, one line a side, and an opposed test's outcome — not the bare dice.
  if (roll.test) return `${nameOf(roll.actor)} — ${roll.label}:\n${testLines(roll.test).join('\n')}`;
  return roll.attack ? `${attackHeadName(roll, meId, nameOf)} — ${roll.label}:\n${dmAttackLines(roll).join('\n')}`
    : roll.save ? `${nameOf(roll.actor)} — ${dmSaveLines(roll).slice(0, roll.save.damage ? 2 : 1).join('\n')}` : formatRoll(roll, nameOf);
}

/**
 * How the log names a person (issue 1023): "You", a member by their character and handle, someone who has left by the
 * handle their roll carries, else "a player"; an account that no longer exists is "a former player" (design/24 §5.1).
 */
export function logNamer(meId: string, members: readonly TableMember[], objects: Record<string, unknown>): (a: Actor) => string {
  return (a) => {
    if (a.id === meId) return 'You';
    if (a.former) return 'a former player';
    const m = members.find((x) => x.id === a.id);
    // A player's plain roll names the character the server credited it to (issue 1553, Tom's choice A); credited to none,
    // a player with two characters is named by their handle alone, never by whichever character comes first.
    if (m && m.role === 'player' && a.character !== undefined) {
      const credited = a.character ? m.characters.find((c) => c.id === a.character) : undefined;
      if (credited) return memberLabel(m, objects as never, { about: { owners: [m.id], character: credited.id } });
      if (m.characters.length > 1) return m.handle ? `@${m.handle}` : m.displayName?.trim() || 'a player';
    }
    return m ? memberLabel(m, objects as never) : a.handle ? `@${a.handle}` : 'a player';
  };
}

/**
 * A log entry as one line of speech (issue 1175): exactly the words the entry shows this viewer — the player's version of
 * an attack or a save, "(private)" where the screen marks it — and nothing for a cleared view.
 */
export function logEntrySpeech(e: LogEntry, meId: string, nameOf: (a: Actor) => string, members: readonly TableMember[]): string | null {
  const flat = (lines: string[]) => lines.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  if (e.kind === 'clear') return null;
  if (e.kind === 'attack') return flat(playerAttackLines(e.attack));
  if (e.kind === 'save') return flat(playerSaveLines(e.save));
  if (e.kind === 'trigger') return flat([...e.trigger.lines, e.trigger.ask ? e.trigger.ask.question : '']);
  if (e.kind === 'note' && e.rule) return `Rules (only you see this): ${e.note}`;
  if (e.kind === 'note' && e.announce) return e.note;
  if (e.kind === 'note') {
    const who = nameOf({ id: e.actorId ?? '', handle: e.handle ?? members.find((m) => m.id === e.actorId)?.handle ?? null, former: e.former === true || e.actorId === null });
    return `${who} pointed: ${e.note}`;
  }
  return flat([...rollText(e.roll, meId, nameOf).split('\n'), e.roll.private ? '(private)' : '']);
}

function Entry({ roll, colourOf, nameOf, canFudge, onFudge, onReroll, onPlace, onSaveNpc, meId, isDm = false }: {
  roll: RollRecord; colourOf: (id: string) => string; nameOf: (a: Actor) => string; canFudge: boolean; onFudge: (total: number) => void;
  meId: string;
  /** The DM's view (issue 1529: the Karma uses of any test). */
  isDm?: boolean;
  /** Place the creature a table's outcome names (issue 1158): the DM's, absent for anyone else or an outcome with none. */
  onPlace?: () => void;
  /** Save a table's result as a campaign NPC (issue 1157): the DM's, on a table line alone. */
  onSaveNpc?: () => void;
  /** Fire it again (issue 1037); absent when this viewer may not. */
  onReroll?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  // An attack (issue 1124) is the DM's whole working, not the bare throw: who, against whom, the defence, the outcome.
  // A template's saves (issue 1129): the spell, its save and damage; each creature is its own row below, with its controls.
  const [head, ...rest] = rollText(roll, meId, nameOf).split('\n');
  // A card edged in the roller's colour (issue 1541): who and what on top with the time, the working muted under it, and a
  // plain throw's total large. The name keeps the roller's colour too, so the colour-blind palette still tells rollers
  // apart by more than a 3 px edge. The total is drawn first but written last, so the entry's words read as before.
  const plain = !roll.attack && !roll.save && !roll.test && !roll.trigger && !roll.table && roll.die.kind !== 'coin';
  const when = clockOf(roll.at);
  return (
    <li className={`vtt-log-card relative min-w-0 text-xs ${roll.private ? 'bg-rose-950/30' : ''}`} style={{ borderLeftColor: colourOf(roll.actor.id) }} data-testid={`roll-${roll.id}`} data-private={roll.private ? 'yes' : 'no'}>
      {/* The words in the order they always read (who, then the working); the time and the total sit where the card draws
          them, after the words in the page, each behind a line break no one sees, so the entry's text is as before. */}
      <span className={`block whitespace-pre-wrap break-words text-[0.6875rem] font-semibold ${when ? 'pr-14' : ''}`} style={{ color: colourOf(roll.actor.id) }}>{head}</span>
      {'\n'}
      {rest.length > 0 && (
        <div className="mt-0.5 flex items-baseline gap-2">
          <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[0.71875rem] text-stone-400">{rest.join('\n')}</span>
          {'\n'}
          {plain && <b className="order-first text-xl font-bold leading-6 text-stone-100" aria-hidden="true" data-testid={`log-total-${roll.id}`}>{roll.fudged?.total ?? roll.total}</b>}
        </div>
      )}
      {when && <>{'\n'}<time className="absolute right-2 top-1.5 text-[0.625rem] text-stone-400" dateTime={roll.at} data-testid={`log-time-${roll.id}`}>{when}</time></>}
      {(roll.private || roll.by || roll.band || canFudge || onReroll || onPlace || onSaveNpc || mayApply(roll, meId, isDm)) && (
        <div className="flex flex-wrap items-center gap-1 text-[0.625rem]">
          {roll.private && <span className="text-rose-400" title={privateTitle(roll, meId, nameOf)}>(private)</span>}
          {roll.band && <span className="text-amber-400" title="Thrown in your fudge band: each number in it equally likely. Players never see this.">(banded {roll.band.min}–{roll.band.max})</span>}
          {roll.by && <span className="text-stone-500">(rolled by {nameOf(roll.by)})</span>}
          {onReroll && <button type="button" className={btn} aria-label={`Roll ${roll.notation}${roll.label ? ` (${roll.label})` : ''} again`} title={roll.by ? `Again, for ${nameOf(roll.actor)}, secret as before.` : roll.private ? 'Again, private as before.' : 'Again, in public.'} onClick={onReroll}>Roll again</button>}
          {onPlace && roll.table?.ref && <button type="button" className={btn} data-testid={`roll-place-${roll.id}`} aria-label={`Place ${roll.table.ref.name} on the board`} title="Opens the compendium's Place: the name players see, hidden or not, the turn order." onClick={onPlace}>Place {roll.table.ref.name}</button>}
          {onSaveNpc && roll.table && <button type="button" className={btn} data-testid={`roll-save-npc-${roll.id}`} aria-label={`Save ${roll.table.text} as an NPC`} title="Saves it to the campaign's NPCs on the site, the name and a note yours to change first. The players are told nothing." onClick={onSaveNpc}>Save as NPC…</button>}
          <ApplyRoll roll={roll} meId={meId} isDm={isDm} />
          {canFudge && !editing && <button type="button" className={btn} aria-label={`Change the result of ${roll.notation}`} onClick={() => { setValue(String(roll.fudged?.total ?? roll.total)); setEditing(true); }}>Change…</button>}
        </div>
      )}
      {roll.attack && roll.attack.ac === null && !roll.attack.staged && !roll.attack.spell && <SupplyDefence rollId={roll.id} defence={roll.attack.rules.defence} />}
      {roll.attack && !roll.attack.staged && !roll.attack.spell && <HouseRuleParts roll={roll} meId={meId} />}
      {roll.attack?.staged && <StagedDmParts roll={roll} />}
      {roll.test && <KarmaParts roll={roll} meId={meId} isDm={isDm} />}
      {roll.attack?.full && <DmFullSwing roll={roll} />}
      {roll.attack && <DamageProposal roll={roll} />}
      {roll.save && <SaveRows roll={roll} />}
      <AppliedRows roll={roll} nameOf={nameOf} isDm={isDm} />
      {roll.trigger && <TriggerRows roll={roll} />}
      {editing && (
        <form className="mt-1 flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); const n = Number(value); if (Number.isFinite(n)) { onFudge(Math.trunc(n)); setEditing(false); } }}>
          <input aria-label="New result" type="number" className="w-16 rounded bg-stone-800 px-1 py-0.5 text-xs" value={value} onChange={(e) => setValue(e.target.value)} />
          <button type="submit" className={btn}>Set</button>
          <button type="button" className={btn} onClick={() => setEditing(false)}>Cancel</button>
        </form>
      )}
    </li>
  );
}

/** An asked player's prompt (issue 1049): the DM's request, their own modifier when the DM left it to them, and Roll. */
function AskPrompt({ ask, onRoll }: { ask: PlayerAsk; onRoll: (modifier: number) => void }) {
  const [mod, setMod] = useState('0');
  const own = ask.modifier === null && ask.die.kind !== 'coin';
  return (
    <li className="flex flex-wrap items-center gap-1 rounded border border-sky-700 bg-sky-950/60 px-2 py-1 text-xs" data-testid={`ask-${ask.id}`}>
      <span className="font-semibold">The DM asks for {ask.label}</span>
      <span className="text-stone-400">{notationOf(ask.die, ask.quantity, ask.modifier ?? 0)}{own ? ' + your modifier' : ''}{ask.public ? ' · the table sees it' : ' · only you and the DM see it'}</span>
      {own && <input aria-label={`Your modifier for ${ask.label}`} type="number" min={-DICE_LIMITS.modifier} max={DICE_LIMITS.modifier} className="w-12 rounded bg-stone-800 px-1 py-0.5" value={mod} onChange={(e) => setMod(e.target.value)} />}
      <button type="button" className={btn} aria-label={`Roll ${ask.label}`} onClick={() => onRoll(Math.max(-DICE_LIMITS.modifier, Math.min(DICE_LIMITS.modifier, Math.trunc(Number(mod) || 0))))}>Roll</button>
    </li>
  );
}

/** The DM's request form (issue 1049): a title, the die and how many, a fixed modifier or each player's own, who, and whether the table sees the results. */
function AskForm({ players, onSend, onDone }: { players: Array<{ id: string; name: string }>; onSend: (ask: Omit<RollAsk, 'answered' | 'id'>) => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [sides, setSides] = useState('20');
  const [count, setCount] = useState('1');
  const [own, setOwn] = useState(true);
  const [fixed, setFixed] = useState('0');
  const [chosen, setChosen] = useState<string[]>([]);
  const [pub, setPub] = useState(false);
  const ready = title.trim() !== '' && chosen.length > 0;
  return (
    <form className="space-y-1 rounded border border-sky-700 p-2 text-xs" data-testid="ask-form" onSubmit={(e) => {
      e.preventDefault();
      if (!ready) return;
      onSend({
        label: title.trim(), die: { kind: 'd', sides: Math.max(2, Math.min(DICE_LIMITS.sides, Math.floor(Number(sides) || 20))) },
        quantity: Math.max(1, Math.min(DICE_LIMITS.quantity, Math.floor(Number(count) || 1))), modifier: own ? null : Math.trunc(Number(fixed) || 0), to: chosen, public: pub,
      });
      onDone();
    }}>
      <input aria-label="Request title" placeholder="Reflex save" maxLength={DICE_LIMITS.label} className="w-full rounded bg-stone-800 px-2 py-1" value={title} onChange={(e) => setTitle(e.target.value)} />
      <div className="flex flex-wrap items-center gap-1">
        <input aria-label="Number of dice to request" type="number" min={1} max={DICE_LIMITS.quantity} className="w-12 rounded bg-stone-800 px-1 py-0.5" value={count} onChange={(e) => setCount(e.target.value)} />
        d<input aria-label="Sides of the requested die" type="number" min={2} max={DICE_LIMITS.sides} className="w-14 rounded bg-stone-800 px-1 py-0.5" value={sides} onChange={(e) => setSides(e.target.value)} />
        <label className="flex items-center gap-1"><input type="checkbox" checked={own} onChange={(e) => setOwn(e.target.checked)} />Players add their own modifier</label>
        {!own && <input aria-label="Fixed modifier" type="number" className="w-14 rounded bg-stone-800 px-1 py-0.5" value={fixed} onChange={(e) => setFixed(e.target.value)} />}
      </div>
      <fieldset className="flex flex-wrap gap-2">
        <legend className="text-stone-400">Who</legend>
        {players.length === 0 && <span className="text-stone-500">No players at the table.</span>}
        {players.map((p) => (
          <label key={p.id} className="flex items-center gap-1"><input type="checkbox" aria-label={`Ask ${p.name}`} checked={chosen.includes(p.id)}
            onChange={(e) => setChosen((cur) => (e.target.checked ? [...cur, p.id] : cur.filter((x) => x !== p.id)))} />{p.name}</label>
        ))}
      </fieldset>
      <label className="flex items-center gap-1"><input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)} />Public request: the table sees the results</label>
      <div className="flex gap-1">
        <button type="submit" className={btn} disabled={!ready}>Send request</button>
        <button type="button" className={btn} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

export function DicePanel() {
  const { table, campaignId } = useShell();
  const { act, role, meId, status, log, logMore, loadOlderLog, setNotice, store, asks: given, systemRules } = useTableState();
  const words = useNumberWords();
  const asks = given ?? {};
  const [asking, setAsking] = useState(false);
  const [attacking, setAttacking] = useState(false);
  const [testing, setTesting] = useState(false);
  // The ranged attack, staged (issue 1527): only where the table's system names one.
  const [ranged, setRanged] = useState(false);
  // Melee (issue 1528): only where the system's staged attack names one.
  const [meleeOpen, setMeleeOpen] = useState(false);
  const [casting, setCasting] = useState(false);
  const [conjuring, setConjuring] = useState(false);
  const [decking, setDecking] = useState(false);
  const [bringing, setBringing] = useState(false);
  const [driving, setDriving] = useState(false);
  // Vehicle combat (issue 1174): only where the table's system has its block.
  const vehicleCombat = useVehicleCombat();
  const [vehicleFighting, setVehicleFighting] = useState(false);
  const [rbTitle, setRbTitle] = useState('');
  // The button being made (issue 1295): one or more parts, each a die, a count, a modifier and an optional label.
  const [rbParts, setRbParts] = useState<RbPart[]>([NEW_PART]);
  const setPart = (i: number, change: Partial<RbPart>) => setRbParts((cur) => cur.map((p, j) => (j === i ? { ...p, ...change } : p)));
  // The DM's dice — private throws, rolling for someone, the band, the fudge, asks, clearing the log — are `dice`'s
  // (issues 1159, 1160); placing a table's creature is `tokens`', saving it to the roster `notes`', the log's text the DM's.
  const mayAct = useMayAct(); const holds = useHolds();
  const isDm = mayAct('roll.amend');
  const [open, setOpen] = useState(readOpen);
  const [tab, setTab] = useState<'mine' | 'add'>('mine');
  const [bag, setBag] = useState<Bag | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  const [label, setLabel] = useState('');
  // The Private choice is one setting for the page (issue 1158): the Tables panel rolls by it too.
  const [priv, setPriv] = useDicePrivate();
  const [placing, setPlacing] = useState<{ segment: string; slug: string; name: string } | null>(null);
  // A large throw asks in the page, never window.confirm (issue 1431): a browser that suppresses native dialogs threw nothing.
  const [bigRoll, setBigRoll] = useState<{ count: number; go: () => void } | null>(null);
  const askFirst = (count: number, go: () => void) => { if (count > CONFIRM_ABOVE) setBigRoll({ count, go }); else go(); };
  const [savingNpc, setSavingNpc] = useState<string | null>(null);
  const [forUser, setForUser] = useState('');
  const [showCleared, setShowCleared] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSides, setNewSides] = useState('3');
  // The expression field (issue 1541): what is typed, and why the last one could not be thrown.
  const [expr, setExpr] = useState('');
  const [exprError, setExprError] = useState<string | null>(null);
  // A one-off extra modifier (qa-runs#368): "+2 circumstance", added to the next throw and then cleared.
  const [extraText, setExtraText] = useState('');
  const extra = parseExtra(extraText);
  // The attack the Attacks section picked (qa-runs#368): the form opens with it, at the token it belongs to.
  const [attackPick, setAttackPick] = useState<{ token: string; key: string } | null>(null);
  // The selected token the sections follow (qa-runs#368): one token, any for the DM, a player's own for a player.
  const [selected, setSelected] = useState(selection.get());
  useEffect(() => selection.subscribe(setSelected), []);
  const exprRef = useRef<HTMLInputElement>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logEnd = useRef<HTMLDivElement>(null);
  const logList = useRef<HTMLOListElement>(null);
  // Whether the reader is at, or near, the newest roll (issue 1419): the log follows a new roll only then, so someone
  // scrolled up to read older lines is left where they are. The bag opens at the newest.
  const atNewest = useRef(true);

  useEffect(() => { void fetchBag().then(setBag); }, []);
  useEffect(() => { try { localStorage.setItem(OPEN_KEY, open ? 'open' : 'closed'); } catch { /* a private window */ } }, [open]);

  // The bag is saved as it changes, a beat after the last change.
  const update = useCallback((next: Bag) => {
    setBag(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { void saveBag(next); }, 400);
  }, []);

  const members = table.members;
  const look = useLook();
  const colourOf = (id: string) => look.identity(members.find((m) => m.id === id)?.colour ?? '#a8a29e'); // the person's look (issue 1041)
  // An account that no longer exists is "a former player", never the handle it had (design/24 §5.1). A member is named
  // by their character and handle (issue 1023); someone who has left by the handle their roll carries, else "a player".
  const nameOf = logNamer(meId, members, store.state.objects);

  // The token a player has selected rides on their throws (issue 1553): the server credits the roll to its character.
  const asToken = (): { token?: string } => {
    if (role !== 'player') return {};
    const id = [...selection.get()][0];
    return id ? { token: id } : {};
  };
  /** The extra modifier on a throw (qa-runs#368): its number added, its words after the label; null when none or unreadable. */
  const withExtra = <T extends { modifier: number; label: string }>(req: T): T => {
    if (!extra || extra === 'bad') return req;
    const label = `${req.label ? `${req.label} ` : ''}(${extra.note})`.slice(0, DICE_LIMITS.label);
    return { ...req, modifier: Math.max(-DICE_LIMITS.modifier, Math.min(DICE_LIMITS.modifier, req.modifier + extra.value)), label };
  };
  const roll = (row: DiceRow, sides: number | null, as?: string, advantage?: AdvantageWord, check?: RollRequest['check']) => {
    if (extra === 'bad') { setNotice('The extra modifier reads like +2 circumstance: a whole number first.'); return; }
    if (extra) setExtraText('');
    // A saved roll button (issue 1049) throws what it was saved with, under its own title; privacy and Roll for still apply.
    // One of several parts (issue 1295) is sent only once the table has accepted the one before, so the log keeps its order.
    // The extra modifier rides on a saved roll button's first part (its attack, say), never on each of them.
    const saved = savedRollRequests(row, isDm ? priv : false, isDm && forUser ? forUser : null, isDm ? bag?.band ?? null : null).map((r, i) => (i === 0 && row.die.kind !== 'coin' ? withExtra(r) : r));
    if (saved.length) {
      const next = (i: number) => {
        if (i >= saved.length) return;
        act({ ...saved[i], ...asToken() } as never, (reply) => {
          if (reply.ok) { next(i + 1); return; }
          if (reply.error === 'view_only') setNotice("The DM is away — the bag can't throw right now.");
          else if (i > 0) setNotice(`${row.label}: the rest was not thrown.`);
        });
      };
      askFirst(Math.max(row.quantity, ...saved.map((r) => r.quantity)), () => next(0));
      return;
    }
    const die = row.die.kind === 'coin' ? row.die : { kind: 'd' as const, sides: sides ?? row.die.sides };
    const plain = { kind: 'roll.request' as const, die, quantity: row.quantity, modifier: row.die.kind === 'coin' ? 0 : row.modifier, label: (as ?? label.trim()).slice(0, DICE_LIMITS.label), private: isDm ? priv : false, forUserId: isDm && forUser ? forUser : null, ...asToken() };
    const base = row.die.kind === 'coin' ? plain : withExtra(plain);
    // The DM's fudge band (issue 1038) rides on their private d20 throws alone, set in advance in the bag.
    // A word for advantage is its own throw: the server refuses a band beside it.
    const asked = advantage ? { ...base, advantage } : isDm && bag?.band && bandApplies(base, 'dm') ? { ...base, band: bag.band } : base;
    const body = check ? { ...asked, check } : asked;
    askFirst(row.quantity, () => act(body as never, (reply) => {
      if (reply.ok) return;
      if (reply.error === 'view_only') setNotice("The DM is away — the bag can't throw right now.");
    }));
  };

  // A logged roll fired again (issue 1037): the same request as before, thrown by the server; privacy and Roll for carry over.
  const reroll = (req: RollRequest) => {
    askFirst(req.quantity, () => act({ ...req, ...asToken() } as never, (reply) => {
      if (reply.ok) return;
      if (reply.error === 'view_only') setNotice("The DM is away — the bag can't throw right now.");
    }));
  };

  // A group roll request (issue 1049): the DM sends it; an asked player answers it as their own roll, made by the server from the ask.
  const answer = (ask: PlayerAsk, modifier: number) => {
    act({ kind: 'roll.request', die: ask.die, quantity: ask.quantity, modifier, label: ask.label, private: false, forUserId: null, ask: ask.id, ...asToken() } as never, (reply) => {
      if (reply.ok) return;
      setNotice(reply.error === 'view_only' ? "The DM is away — the bag can't throw right now." : reply.reason ?? 'That roll could not be made.');
    });
  };
  const sendAsk = (ask: Omit<RollAsk, 'answered' | 'id'>) => {
    act({ kind: 'roll.ask', ask: { ...ask, id: crypto.randomUUID() } } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'The request could not be sent.'); });
  };
  const openAsks = Object.values(asks);

  // What each row last threw: for every row, the newest roll in the log by this person that it threw (issue 1225: after
  // a reload each row comes back with its own, not only the row of the newest roll). A saved roll button's throw carries
  // its title as the label and lands on that button, never on a plain row with the same dice; anything else lands on the
  // plain row with its notation. Several coins show the tally the log uses (design/15 §4; issue 1214).
  useEffect(() => {
    if (!bag) return;
    const found: Record<string, string> = {};
    for (let i = log.length - 1; i >= 0; i -= 1) {
      const e = log[i]!;
      if (e.kind !== 'roll' || e.roll.attack || (e.roll.by ?? e.roll.actor).id !== meId) continue;
      const r = e.roll;
      const same = (row: DiceRow) => notationOf(row.die, row.quantity, row.die.kind === 'coin' ? 0 : row.modifier) === r.notation;
      // A several-roll button (issue 1295) shows its newest part's result: its throws are labelled "<title>: …".
      const row = bag.rows.find((x) => x.label && !x.parts && x.label === r.label && same(x))
        ?? bag.rows.find((x) => x.label && x.parts && r.label.startsWith(`${x.label}: `))
        ?? bag.rows.find((x) => !x.label && same(x));
      if (!row || found[row.id] !== undefined) continue;
      found[row.id] = r.die.kind === 'coin' ? (r.dice.length === 1 ? String(r.dice[0]) : tally(r.dice)) : String(r.fudged?.total ?? r.total);
    }
    if (Object.keys(found).length) setResults((prev) => ({ ...prev, ...found }));
  }, [log, bag, meId]);

  // What each quick die of the grid last threw (issue 1541): this person's newest plain throw of one of it, from the log,
  // and which of them was thrown last — that one wears the gold edge.
  const grid = useMemo(() => {
    const found: Record<number, string> = {};
    let last: number | null = null;
    for (let i = log.length - 1; i >= 0; i -= 1) {
      const e = log[i]!;
      if (e.kind !== 'roll') continue;
      const r = e.roll;
      if (r.attack || r.save || r.test || r.trigger || r.table || (r.by ?? r.actor).id !== meId || r.die.kind !== 'd' || r.dice.length !== 1 || r.modifier !== 0) continue;
      const sides = r.die.sides;
      if (!(GRID_SIDES as readonly number[]).includes(sides) || found[sides] !== undefined) continue;
      found[sides] = String(r.fudged?.total ?? r.total);
      if (last === null) last = sides;
    }
    return { found, last };
  }, [log, meId]);
  // One of a die from the grid, or the typed expression: a row of its own for this throw, sent as any row is.
  const throwOne = (die: DiceRow['die'], quantity: number, modifier: number) => roll({ id: 'quick', die, name: null, custom: false, quantity, modifier }, null);
  // A section's check (qa-runs#368): the system's save dice plus the token's number, labelled with what it is.
  const rollCheck = (as: string, dice: { count: number; sides: number }, total: number, advantage?: AdvantageWord, check?: RollRequest['check']) =>
    roll({ id: 'check', die: { kind: 'd', sides: dice.sides }, name: null, custom: false, quantity: dice.count, modifier: total }, null, as, advantage, check);
  const sectionToken = (() => {
    if (selected.size !== 1) return null;
    const o = (store.state.objects as Record<string, { id: string; kind: string; name?: string; mine?: boolean }>)[[...selected][0]!];
    if (!o || o.kind !== 'token' || !(isDm || o.mine)) return null;
    return o;
  })();
  const rollTyped = () => {
    const got = parseExpression(expr);
    if ('error' in got) { setExprError(got.error); return; }
    setExprError(null);
    throwOne(got.die, got.quantity, got.modifier);
  };

  // The view: everything after the last clear marker, unless cleared rolls are shown.
  const visible = useMemo(() => {
    if (showCleared) return log;
    let cut = -1;
    log.forEach((e, i) => { if (e.kind === 'clear') cut = i; });
    return cut === -1 ? log : log.slice(cut);
  }, [log, showCleared]);
  const clearedCount = log.length - visible.length;
  // The newest line is the list's last (the log reads oldest first). Older rolls loaded above leave it unchanged.
  const newest = visible.length ? visible[visible.length - 1] : null;
  useLayoutEffect(() => {
    const el = logList.current;
    if (!el || !atNewest.current) return;
    el.scrollTop = el.scrollHeight;
    logEnd.current?.scrollIntoView?.({ block: 'nearest' });
  }, [newest, open]);

  // The bag's rows of one kind — the saved roll buttons, or the dice — each moved among its own kind (issue 1541: the two
  // are shown apart now); the bag keeps one order underneath, as it always has.
  const rowsOf = (saved: boolean) => {
    if (!bag) return null;
    const mine = bag.rows.map((row, i) => ({ row, i })).filter(({ row }) => Boolean(row.label) === saved);
    return mine.map(({ row, i }, k) => (
      <Row key={row.id} row={row} result={results[row.id] ?? null} first={k === 0} last={k === mine.length - 1}
        onChange={(r) => update({ ...bag, rows: bag.rows.map((x) => (x.id === r.id ? r : x)) })}
        onRoll={roll}
        onRemove={() => update({ ...bag, rows: bag.rows.filter((x) => x.id !== row.id) })}
        onMove={(d) => { const to = mine[k + d]; if (!to) return; const rows = [...bag.rows]; [rows[i], rows[to.i]] = [rows[to.i]!, rows[i]!]; update({ ...bag, rows }); }} />
    ));
  };

  if (!open) {
    return (
      <div className="flex h-full items-start p-2" data-testid="dice-collapsed">
        <button type="button" className="flex h-12 w-12 items-center justify-center rounded-lg border-2 border-amber-500 bg-stone-800 text-2xl hover:bg-stone-700" aria-label="Open the dice bag" title="Dice bag" onClick={() => { atNewest.current = true; setOpen(true); }}>🎲</button>
      </div>
    );
  }

  const forName = forUser ? memberLabel(members.find((m) => m.id === forUser), store.state.objects) : '';
  const privateNow = isDm && (priv || forUser !== '');
  return (
    <div className={`flex h-full min-w-0 flex-col gap-1 overflow-y-auto p-2 text-sm ${privateNow ? 'bg-[repeating-linear-gradient(135deg,rgba(190,18,60,0.10)_0,rgba(190,18,60,0.10)_8px,transparent_8px,transparent_16px)]' : ''}`} data-testid="dice-bag" data-private={privateNow ? 'yes' : 'no'}>
      {/* Wraps on a narrow bar (a portrait tablet's, under 200 px) rather than pushing Add Dice past the bar's edge. */}
      <div className="flex flex-wrap items-center gap-2 whitespace-nowrap" data-testid="dice-tabs">
        <button type="button" className={btn} aria-label="Collapse the dice bag" onClick={() => setOpen(false)}>🎲 ◂</button>
        <div role="tablist" className="flex gap-1">
          <button type="button" role="tab" aria-selected={tab === 'mine'} className={`${btn} ${tab === 'mine' ? 'bg-stone-700' : ''}`} onClick={() => setTab('mine')}>My Dice</button>
          <button type="button" role="tab" aria-selected={tab === 'add'} className={`${btn} ${tab === 'add' ? 'bg-stone-700' : ''}`} onClick={() => setTab('add')}>Add Dice</button>
        </div>
      </div>
      {!isDm && status.viewOnly && <p className="rounded bg-amber-900/60 px-2 py-1 text-xs text-amber-100" data-testid="dice-view-only">The DM is away — the bag can't throw right now.</p>}

      {tab === 'mine' && bag && (
        <>
          <div className="sticky -top-2 z-10 -mx-2 flex flex-wrap items-center gap-1 bg-stone-900 px-2 pb-1 pt-3" data-testid="dice-controls">
            {isDm && (
              // Pinned with the dice it governs (issue 1025, decided A): one toggle, never scrolled out of sight. A roll made
              // for a player is the DM's alone whatever the toggle says (design/15), so the line under it says so instead of
              // PUBLIC. The site's look (issue 1541): two halves, Public in emerald and Private (DM) in rose; the Private half
              // is the checkbox it always was, so the keyboard and every test that ticks it find it where it was.
              <div className="flex w-full flex-col gap-0.5" data-testid="dice-privacy"
                title={forUser ? 'A roll made for a player is seen by you alone, and posted to their name.' : 'Private: your rolls post to your log alone. Public: the table sees them.'}>
                <div role="group" aria-label="Who sees your rolls" className="flex flex-nowrap overflow-hidden rounded-[3px] border border-stone-600 text-[0.71875rem]">
                  <button type="button" aria-pressed={!privateNow} disabled={forUser !== ''} onClick={() => setPriv(false)}
                    className={`flex-1 px-2 py-1 text-center ${!privateNow ? 'bg-emerald-900 font-semibold text-emerald-100' : 'text-stone-400 hover:bg-stone-800'} disabled:cursor-not-allowed`}>Public roll</button>
                  <label className={`flex flex-1 cursor-pointer items-center justify-center gap-1 border-l border-stone-600 px-2 py-1 ${privateNow ? 'bg-rose-900 font-semibold text-rose-100' : 'text-stone-400 hover:bg-stone-800'}`}>
                    <input type="checkbox" aria-label="Private" className="accent-rose-500" checked={priv || forUser !== ''} disabled={forUser !== ''} onChange={(e) => setPriv(e.target.checked)} />
                    Private (DM)
                  </label>
                </div>
                <span className={`text-[0.6875rem] font-semibold ${privateNow ? 'text-rose-400' : 'text-emerald-300'}`}>
                  {forUser ? `🕶 SECRET — for ${forName}, only you see it` : privateNow ? '🕶 PRIVATE — only you see these' : '👁 PUBLIC — the table sees these'}
                </span>
              </div>
            )}
            <input aria-label="Label" placeholder="Label (optional)" maxLength={DICE_LIMITS.label} className="min-w-0 flex-1 basis-28 rounded bg-stone-800 px-2 py-1 text-xs" value={label} onChange={(e) => setLabel(e.target.value)} />
            {/* One roll's extra modifier (qa-runs#368): added to the next throw of any kind here, then cleared. */}
            <input aria-label="Extra modifier for one roll" placeholder="+2 circumstance" maxLength={40} title="Added to your next roll from the bag, then cleared: a whole number, then any words."
              className={`min-w-0 basis-24 flex-1 rounded bg-stone-800 px-2 py-1 text-xs ${extra === 'bad' ? 'ring-1 ring-rose-500' : extra ? 'ring-1 ring-amber-500' : ''}`}
              aria-invalid={extra === 'bad' ? true : undefined} value={extraText} onChange={(e) => setExtraText(e.target.value)} data-testid="dice-extra" />
            {isDm && (
              <select aria-label="Roll for" className="min-w-0 max-w-full rounded bg-stone-800 px-1 py-1 text-xs" value={forUser} onChange={(e) => setForUser(e.target.value)} title="Throw on a player's behalf: attributed to them, seen by you alone.">
                <option value="">Roll as myself</option>
                {members.filter((m) => m.role === 'player').map((m) => <option key={m.id} value={m.id}>Roll for {memberLabel(m, store.state.objects)}</option>)}
              </select>
            )}
          </div>
          {/* The selected token's sections (qa-runs#368); with none selected, only Dice. */}
          {sectionToken && (
            <TokenChecks key={sectionToken.id} tokenId={sectionToken.id} name={sectionToken.name || 'Token'} isDm={isDm} onRoll={rollCheck}
              onAttack={(key) => { setAttackPick({ token: sectionToken.id, key }); setAttacking(true); }} />
          )}
          <CheckSection id="dice" title="Dice">
          {/* The quick dice (issue 1541): four across, two down; each throws one of itself, its last result beneath. */}
          <div className="grid grid-cols-4 gap-1.5" data-testid="dice-grid">
            {GRID_SIDES.map((sides) => {
              const last = grid.last === sides;
              return (
                <button key={sides} type="button" aria-label={`Quick roll ${gridFace(sides)}`} {...hotDragProps({ token: null, ref: `dice:1d${sides}`, label: gridFace(sides) })} data-last={last ? 'yes' : 'no'}
                  className={`flex flex-col items-center rounded border bg-stone-800 pb-1 pt-1.5 hover:bg-stone-700 ${last ? 'border-amber-500' : 'border-stone-600'}`}
                  onClick={() => throwOne({ kind: 'd', sides }, 1, 0)}>
                  <span className="font-display text-sm font-bold leading-4 text-vtt-gold">{gridFace(sides)}</span>
                  <span className="text-[0.625rem] leading-3 text-stone-400" data-testid={`grid-result-${sides}`}>{grid.found[sides] ?? '—'}</span>
                </button>
              );
            })}
            <button type="button" aria-label="Type any dice" title="Any dice: type them in the field below, such as 3d6 + 2."
              className="flex flex-col items-center rounded border border-stone-600 bg-stone-800 pb-1 pt-1.5 hover:bg-stone-700" onClick={() => exprRef.current?.focus()}>
              <span className="font-display text-sm font-bold leading-4 text-vtt-gold">+</span>
              <span className="text-[0.625rem] leading-3 text-stone-400">custom</span>
            </button>
          </div>
          {/* Any dice typed, beside the gold ROLL (issue 1541): one request, the same label, privacy and Roll for. */}
          <form className="flex flex-nowrap items-stretch gap-1.5" data-testid="dice-expression" onSubmit={(e) => { e.preventDefault(); rollTyped(); }}>
            <input ref={exprRef} aria-label="Dice to roll" placeholder={`e.g. 1d20 + 2${forName ? ` · for ${forName}` : ''}`} maxLength={60}
              className="min-w-0 flex-1 rounded-[3px] border border-stone-600 bg-stone-950 px-2 py-1.5 text-xs"
              aria-invalid={exprError ? true : undefined} aria-describedby={exprError ? 'dice-expression-error' : undefined}
              value={expr} onChange={(e) => { setExpr(e.target.value); setExprError(null); }} />
            <button type="submit" aria-label="Roll the typed dice" className={`${PRIMARY_SM} shrink-0 px-4 font-display font-bold uppercase tracking-[0.08em]`}>Roll</button>
          </form>
          {exprError && <p id="dice-expression-error" role="alert" className="text-xs text-rose-400" data-testid="dice-expression-error">{exprError}</p>}
          {/* The saved roll buttons (issue 1049) with the quick dice (issue 1541): the named rolls a person fires most. */}
          {bag.rows.some((r) => r.label) && (
            <section aria-label="Saved rolls">
              <h3 className={SECTION_HEAD}>Saved rolls</h3>
              <ul className="divide-y divide-stone-800" data-testid="saved-rolls">{rowsOf(true)}</ul>
            </section>
          )}
          </CheckSection>
          {/* No attack the table can resolve, no Attack… (issue 1305): a system with an attack rule it cannot automate says why. */}
          {!systemRules.attack
            ? (systemRules.attackRefusal && systemRules.attackRefusal !== NO_ATTACK_RULES ? <p className="text-xs text-stone-500" data-testid="attack-refusal">{systemRules.attackRefusal}</p> : null)
            : attacking
              ? <AttackForm key={attackPick ? `${attackPick.token} ${attackPick.key}` : 'typed'} isDm={mayAct('attack.request')} priv={priv} band={bag.band ?? null} onClose={() => { setAttacking(false); setAttackPick(null); }}
                  {...(attackPick ? { attacker: attackPick.token, initialChoice: attackPick.key } : {})} />
              : <button type="button" className={`${btn} self-start`} onClick={() => setAttacking(true)} title={`Roll an attack against a token: the table reads its ${words.defence} and says hit or miss.`}>Attack…</button>}
          {/* A success test (issue 1523): only where the table's system names one, so a d20 table never sees it. */}
          {systemRules.test && (testing
            ? <TestForm isDm={mayAct('test.request')} priv={priv} onClose={() => setTesting(false)} />
            : <button type="button" className={`${btn} self-start`} onClick={() => setTesting(true)} title="A success test: a skill or attribute of a character, or a rating typed in, against a target number. The table counts the successes.">Test…</button>)}
          {systemRules.staged && (ranged
            ? <StagedForm isDm={mayAct('attack.staged')} priv={priv} onClose={() => setRanged(false)} />
            : <button type="button" className={`${btn} self-start`} onClick={() => setRanged(true)} title="A ranged attack from a weapon: the table builds the target number, asks the defender to dodge and resist, and stages the damage.">Ranged attack…</button>)}
          {systemRules.staged?.melee && (meleeOpen
            ? <StagedForm melee isDm={mayAct('attack.staged')} priv={priv} onClose={() => setMeleeOpen(false)} />
            : <button type="button" className={`${btn} self-start`} onClick={() => setMeleeOpen(true)} title="A melee attack: both sides test their melee skill, more successes hits (the defender too), and the one hit resists.">Melee attack…</button>)}
          {/* A thrown blast (qa-runs#1171): only where the table's system has the blast rule page (the launcher says so itself). */}
          <BlastLauncher isDm={mayAct('attack.staged')} priv={priv} className={`${btn} self-start`} />
          {/* Casting a spell (issue 1530): only where the table's system names spellcasting. */}
          {systemRules.spellcasting && (casting
            ? <CastForm isDm={mayAct('spell.cast')} priv={priv} onClose={() => setCasting(false)} />
            : <button type="button" className={`${btn} self-start`} onClick={() => setCasting(true)} title="Cast a known spell: the casting test against the target's attribute, its resistance, and the drain the caster resists.">Cast spell…</button>)}
          {/* Conjuring a spirit (issue 1531): only where the table's system names conjuring. */}
          {systemRules.conjuring && (conjuring
            ? <ConjureForm isDm={mayAct('spirit.conjure')} priv={priv} onClose={() => setConjuring(false)} />
            : <button type="button" className={`${btn} self-start`} onClick={() => setConjuring(true)} title="Conjure a spirit: the conjuring test against its Force, the drain, and the spirit's token on the table.">Conjure…</button>)}
          {/* A System Test (issue 1532): only where the table's system names one. */}
          {systemRules.matrix && (decking
            ? <SystemTestForm isDm={mayAct('matrix.test')} priv={priv} onClose={() => setDecking(false)} />
            : <button type="button" className={`${btn} self-start`} onClick={() => setDecking(true)} title="A System Test: the decker's skill against the host's subsystem, the host's dice against the decker's Detection Factor.">System test…</button>)}
          {/* Vehicles (issue 1533): only where the table's system names them. */}
          {systemRules.vehicles && (bringing
            ? <BringOutForm isDm={mayAct('vehicle.place')} priv={priv} onClose={() => setBringing(false)} />
            : <button type="button" className={`${btn} self-start`} onClick={() => setBringing(true)} title="Bring a vehicle or drone out beside a rigger: its token, with the entry's numbers and its own condition monitor.">Bring out a vehicle…</button>)}
          {systemRules.vehicles && (driving
            ? <VehicleTestForm isDm={mayAct('vehicle.test')} priv={priv} onClose={() => setDriving(false)} />
            : <button type="button" className={`${btn} self-start`} onClick={() => setDriving(true)} title="A Driving or Maneuver test: the driver's vehicle skill (or a drone's Pilot) and pool dice against the vehicle's Handling.">Maneuver test…</button>)}
          {vehicleCombat && (vehicleFighting
            ? <VehicleCombatForm isDm={mayAct('vehicle.hit')} priv={priv} onClose={() => setVehicleFighting(false)} />
            : <button type="button" className={`${btn} self-start`} onClick={() => setVehicleFighting(true)} title="Vehicle combat: gunnery, a hit on a vehicle, a ram, a crash, dump shock and rigging initiative, worked out by the system's rules.">Vehicle combat…</button>)}
          {vehicleCombat && <ChasePanel isDm={isDm} />}
          {!isDm && openAsks.length > 0 && (
            <ul className="space-y-1" data-testid="ask-prompts" aria-label="The DM's requests">
              {(openAsks as PlayerAsk[]).map((a) => <AskPrompt key={a.id} ask={a} onRoll={(m) => answer(a, m)} />)}
            </ul>
          )}
          {isDm && <BandEditor band={bag.band ?? null} onChange={(band) => update({ ...bag, band })} />}
          {isDm && (
            <section className="space-y-1 text-xs" data-testid="ask-requests" aria-label="Roll requests">
              {!asking && <button type="button" className={btn} onClick={() => setAsking(true)} title="Ask chosen players for a roll at once, such as a saving throw.">Ask players to roll…</button>}
              {asking && <AskForm players={members.filter((m) => m.role === 'player').map((m) => ({ id: m.id, name: memberLabel(m, store.state.objects) }))} onSend={sendAsk} onDone={() => setAsking(false)} />}
              {(openAsks as RollAsk[]).map((a) => (
                <div key={a.id} className="rounded border border-sky-800 px-2 py-1" data-testid={`ask-progress-${a.id}`}>
                  <div className="flex items-center gap-1">
                    <span className="font-semibold">{a.label}</span>
                    <span className="text-stone-400">{notationOf(a.die, a.quantity, a.modifier ?? 0)}{a.modifier === null ? ' + own' : ''} · {a.public ? 'public' : 'private'}</span>
                    <button type="button" className={`${btn} ml-auto`} aria-label={`Close the request ${a.label}`} onClick={() => act({ kind: 'roll.ask.close', id: a.id } as never)}>Close</button>
                  </div>
                  <ul className="flex flex-wrap gap-x-2">
                    {(a.to ?? []).map((pid) => {
                      const rollId = a.answered?.[pid];
                      const got = rollId ? log.find((e) => e.kind === 'roll' && e.roll.id === rollId) : undefined;
                      const name = memberLabel(members.find((m) => m.id === pid), store.state.objects);
                      return <li key={pid} data-testid={`ask-${a.id}-${pid}`} data-rolled={rollId ? 'yes' : 'no'}>{name}: {rollId ? `rolled${got && got.kind === 'roll' ? ` ${got.roll.fudged?.total ?? got.roll.total}` : ''}` : 'waiting'}</li>;
                    })}
                  </ul>
                </div>
              ))}
            </section>
          )}
        </>
      )}
      {tab === 'add' && bag && (
        <div className="space-y-2 text-xs" data-testid="add-dice">
          <p className="text-stone-400">The standard set, for restoring a removed die; and a die of any sides, saved as a row of its own.</p>
          <ul className="flex flex-wrap gap-1">
            {standardRows().map((r) => (
              <li key={r.id}>
                <button type="button" className={btn} disabled={bag.rows.some((x) => x.id === r.id)} aria-label={`Add ${faceOf(r)}`} onClick={() => update({ ...bag, rows: [...bag.rows, r] })}>{faceOf(r)}</button>
              </li>
            ))}
          </ul>
          <form className="flex flex-wrap items-center gap-1" onSubmit={(e) => {
            e.preventDefault();
            const sides = Math.max(2, Math.min(DICE_LIMITS.sides, Math.floor(Number(newSides) || 0)));
            const name = newName.trim() || `d${sides}`;
            const id = `custom-${Date.now().toString(36)}`;
            update({ ...bag, rows: [...bag.rows, { id, die: { kind: 'd', sides }, name, custom: true, quantity: 1, modifier: 0 }] });
            setNewName(''); setTab('mine');
          }}>
            <label className="flex items-center gap-1">Sides <input aria-label="New die sides" type="number" min={2} max={DICE_LIMITS.sides} className="w-16 rounded bg-stone-800 px-1 py-0.5" value={newSides} onChange={(e) => setNewSides(e.target.value)} /></label>
            <input aria-label="New die name" placeholder="Name (d3 of doom)" maxLength={40} className="w-36 rounded bg-stone-800 px-2 py-0.5" value={newName} onChange={(e) => setNewName(e.target.value)} />
            <button type="submit" className={btn}>Save as a die</button>
          </form>
          <form className="flex flex-col gap-1" data-testid="save-roll-button" onSubmit={(e) => {
            e.preventDefault();
            const title = rbTitle.trim();
            if (!title || rbParts.length === 0) return;
            // A saved roll button (issue 1049): a labelled row of the bag, stored with the rest of it. Several parts
            // (issue 1295) are kept as its `parts`, the row's own dice being the first's; one part is the 1049 shape.
            const parts = rbParts.map((p) => {
              const sides = Math.max(2, Math.min(DICE_LIMITS.sides, Math.floor(Number(p.sides) || 20)));
              const quantity = Math.max(1, Math.min(DICE_LIMITS.quantity, Math.floor(Number(p.count) || 1)));
              const modifier = Math.max(-DICE_LIMITS.modifier, Math.min(DICE_LIMITS.modifier, Math.trunc(Number(p.mod) || 0)));
              const label = p.label.trim().slice(0, DICE_LIMITS.label);
              return { die: { kind: 'd' as const, sides }, quantity, modifier, ...(label ? { label } : {}) };
            });
            const first = parts[0]!;
            update({ ...bag, rows: [...bag.rows, { id: `saved-${Date.now().toString(36)}`, die: first.die, name: null, custom: false, quantity: first.quantity, modifier: first.modifier, label: title, ...(parts.length > 1 ? { parts } : {}) }] });
            setRbTitle(''); setRbParts([NEW_PART]); setTab('mine');
          }}>
            <input aria-label="Roll button title" placeholder="Longsword" maxLength={DICE_LIMITS.label} className="w-28 rounded bg-stone-800 px-2 py-0.5" value={rbTitle} onChange={(e) => setRbTitle(e.target.value)} />
            {rbParts.map((p, i) => {
              const of = i === 0 ? '' : `, part ${i + 1}`;
              return (
                <div key={i} className="flex flex-wrap items-center gap-1" data-testid={`roll-button-part-${i + 1}`}>
                  <input aria-label={`Roll button part label${of}`} placeholder={i === 0 ? 'attack' : 'damage'} maxLength={DICE_LIMITS.label} className="w-20 rounded bg-stone-800 px-2 py-0.5" value={p.label} onChange={(e) => setPart(i, { label: e.target.value })} />
                  <input aria-label={`Roll button dice${of}`} type="number" min={1} max={DICE_LIMITS.quantity} className="w-12 rounded bg-stone-800 px-1 py-0.5" value={p.count} onChange={(e) => setPart(i, { count: e.target.value })} />
                  d<input aria-label={`Roll button sides${of}`} type="number" min={2} max={DICE_LIMITS.sides} className="w-14 rounded bg-stone-800 px-1 py-0.5" value={p.sides} onChange={(e) => setPart(i, { sides: e.target.value })} />
                  +<input aria-label={`Roll button modifier${of}`} type="number" className="w-12 rounded bg-stone-800 px-1 py-0.5" value={p.mod} onChange={(e) => setPart(i, { mod: e.target.value })} />
                  {rbParts.length > 1 && <button type="button" className={tiny} aria-label={`Remove part ${i + 1}`} onClick={() => setRbParts((cur) => cur.filter((_, j) => j !== i))}>×</button>}
                </div>
              );
            })}
            <span className="flex flex-wrap gap-1">
              <button type="button" className={btn} disabled={rbParts.length >= BAG_LIMITS.parts} onClick={() => setRbParts((cur) => [...cur, NEW_PART])}>Add part</button>
              <button type="submit" className={btn}>Save roll button</button>
            </span>
          </form>
          <button type="button" className={btn} onClick={() => update({ ...defaultBag(), ...(bag.band ? { band: bag.band } : {}) })}>Restore the whole standard set</button>
        </div>
      )}
      {!bag && <p className="text-xs text-stone-500">Fetching your dice…</p>}

      <section className="mt-1 flex flex-col border-t border-stone-600 pt-2" data-testid="dice-log" aria-label="Roll log">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <h3 className={TITLE_CAPS}>Roll log</h3>
          {logMore && <button type="button" className={btn} onClick={() => void loadOlderLog()}>Older rolls</button>}
          {clearedCount > 0 && !showCleared && <button type="button" className={btn} onClick={() => setShowCleared(true)}>Show cleared rolls</button>}
          {showCleared && clearedCount === 0 && log.some((e) => e.kind === 'clear') && <button type="button" className={btn} onClick={() => setShowCleared(false)}>Hide cleared rolls</button>}
          {isDm && (
            <span className="ml-auto flex gap-1">
              <button type="button" className={btn} onClick={() => act({ kind: 'log.clear' })} title="Clears the view for everyone. Nothing is deleted; anyone can show the cleared rolls again.">Clear view</button>
              {holds('dm') && <button type="button" className={btn} onClick={async () => {
                const text = await fetchLogText(campaignId);
                if (text === null) { setNotice('The log could not be saved.'); return; }
                const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
                const a = document.createElement('a'); a.href = url; a.download = `${table.name.replace(/[^\w.-]+/g, '_')}-rolls.txt`; a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }} title="Everything, your private rolls included.">Save the log</button>}
            </span>
          )}
        </div>
        <ol ref={logList} className="max-h-80 overflow-auto" data-testid="log-entries" aria-label="Roll log" tabIndex={0}
          onScroll={(e) => { const el = e.currentTarget; atNewest.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 24; }}>
          {visible.length === 0 && <li className="py-2 text-xs text-stone-500">No rolls yet.</li>}
          {visible.map((e, i) => withRewoundMark(e, e.kind === 'clear'
            ? <li key={`clear-${i}`} className="py-1 text-center text-[0.625rem] uppercase tracking-wide text-stone-500" data-testid="log-cleared">— view cleared —</li>
            : e.kind === 'attack'
            ? <PlayerAttackEntry key={e.attack.id} attack={e.attack} colour={colourOf(e.attack.actor.id)} />
            : e.kind === 'save'
            ? <PlayerSaveEntry key={e.save.id} save={e.save} colour={colourOf(e.save.actor.id)} />
            : e.kind === 'trigger'
            ? <PlayerTriggerEntry key={e.trigger.id} trigger={e.trigger} />
            : e.kind === 'note' && e.rule
            ? <li key={`note-${i}`} className="vtt-log-card text-xs text-red-300" data-testid="log-rule" style={{ borderLeftColor: '#ef4444' }}><span className="text-stone-400">Rules (only you see this):</span> {e.note}</li>
            : e.kind === 'note' && e.announce
            ? <li key={`note-${i}`} className="vtt-log-card text-xs text-stone-300" data-testid="log-announce" style={{ borderLeftColor: '#78716c' }}>{e.note}</li>
            : e.kind === 'note'
            ? <li key={`note-${i}`} className="vtt-log-card text-[0.78125rem]" data-testid="log-note" style={{ borderLeftColor: colourOf(e.actorId ?? '') }}><span className="text-stone-400">{nameOf({ id: e.actorId ?? '', handle: e.handle ?? members.find((m) => m.id === e.actorId)?.handle ?? null, former: e.former === true || e.actorId === null })} pointed:</span> {e.note}</li>
            : <Entry key={e.roll.id} roll={e.roll} colourOf={colourOf} nameOf={nameOf} meId={meId} isDm={isDm}
                onReroll={(() => { const req = e.roll.attack || e.roll.trigger ? null : rerollOf(e.roll, meId, isDm); return req ? () => reroll(req) : undefined; })()}
                onPlace={isDm && mayAct('object.add') && e.roll.table?.ref ? () => setPlacing(e.roll.table!.ref!) : undefined}
                onSaveNpc={isDm && holds('notes') && e.roll.table ? () => setSavingNpc(e.roll.table!.text) : undefined}
                canFudge={isDm && e.roll.private && !e.roll.save && !e.roll.table && !e.roll.trigger && !e.roll.test && (e.roll.by ?? e.roll.actor).id === meId} onFudge={(total) => act({ kind: 'roll.amend', rollId: e.roll.id, total })} />))}
        </ol>
        <div ref={logEnd} />
      </section>
      {/* The bag's own rows (design/15 §2) under the log (issue 1541): each die with its count and modifier, the custom dice,
          Reset my dice. The grid and the expression above throw the common rolls; these keep everything the bag could do. */}
      {tab === 'mine' && bag && (
        <>
          <h3 className={`${SECTION_HEAD} mt-1 border-t border-stone-800 pt-2`}>My dice</h3>
          <ul className="divide-y divide-stone-800" data-testid="my-dice">
            {bag.rows.length === 0 && <li className="py-2 text-xs text-stone-500">No dice in the bag. Add Dice has the standard set.</li>}
            {rowsOf(false)}
          </ul>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className={btn} onClick={() => { update(resetBag(bag)); setResults({}); }}>Reset my dice</button>
            {/* The turn nudge's opt-in (issue 1050): a player's, asked of the browser only on this click. */}
            {!isDm && <TurnNotifyToggle />}
            {/* Push to this device (issue 1182): the turn, and being named co-DM, even with the table closed. Hidden without the server's keys. */}
            <PushToggle />
          </div>
        </>
      )}
      {placing && <PlaceDialog entry={placing} at={null} tracker={false} onClose={() => setPlacing(null)} />}
      {savingNpc !== null && <SaveNpcDialog name={savingNpc} onClose={() => setSavingNpc(null)} />}
      {bigRoll && (
        <ConfirmDialog title={`Roll ${bigRoll.count} dice?`} body={null} confirmLabel="Roll them" cancelLabel="Cancel"
          onClose={() => setBigRoll(null)} onConfirm={() => { const b = bigRoll; setBigRoll(null); b.go(); }} />
      )}
    </div>
  );
}
